// frontend/src/components/warmup/WarmupSection.tsx
import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Flame, CircleNotch, Pause, Play, Power, Plugs, ArrowsClockwise, WarningCircle, Key, Lightbulb } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { warnBannerClass, iconChipAmberClass, bannerTextStyle } from '@/lib/styleKit'
import { warmupApi, getErrorMessage } from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import { toast } from 'sonner'
import { usePermission } from '@/hooks/usePermission'
import type { WarmupAccount, WarmupProvider as WarmupProviderType, ConnectWarmupData } from '@/types/warmup'
import type { SenderConnectionType } from '@/types/senderEmail'

interface Props {
  senderEmailId: number
  smtpVerified: boolean
  connectionType: SenderConnectionType
}

/** How often to poll while Instantly's own OAuth consent screen is open in
 * the other tab. Their session lives 10 minutes; this just needs to notice
 * "done" reasonably quickly without hammering the endpoint. */
const OAUTH_POLL_INTERVAL_MS = 3000

/**
 * The single action a connected mailbox offers, or null when there is none.
 *
 * A provider's warmup is either running or not, so exactly one of Pause and
 * Resume applies at any moment. 'completed' used to offer NEITHER: the card
 * rendered its blue pill and no button, so a mailbox that reached it looked
 * dead with no way back. Warmbox does report a genuine finished state, and a
 * finished warmup can legitimately be run again, so it offers Restart.
 */
export function warmupAction(status: string): { kind: 'pause' | 'start'; label: string } | null {
  switch (status) {
    case 'warming':
      return { kind: 'pause', label: 'Pause' }
    case 'paused':
      return { kind: 'start', label: 'Resume' }
    case 'completed':
      return { kind: 'start', label: 'Restart Warmup' }
    case 'inactive':
    case 'error':
      return { kind: 'start', label: 'Start Warmup' }
    default:
      return null
  }
}

const PROVIDERS: { value: WarmupProviderType; label: string }[] = [
  { value: 'instantly', label: 'Instantly' },
  // Warmbox and Lemwarm are hidden from the picker, not removed. Their adapters
  // were fixed recently but have not been tested against the live providers, so
  // offering them would invite a user to connect a mailbox through a path
  // nobody has exercised. The backend still accepts and runs both, and an
  // existing connection on either keeps working and keeps showing its card --
  // only the choice of a NEW one is withheld.
  //
  // Put the line back when they have been verified end to end.
  // { value: 'warmbox', label: 'Warmbox' },
  // { value: 'lemwarm', label: 'Lemwarm' },
]

const WarmupSection: React.FC<Props> = ({ senderEmailId, smtpVerified, connectionType }) => {
  // Nylas-brokered mailboxes have no raw SMTP/IMAP password to give the
  // ordinary Connect form -- Instantly's own OAuth (a separate consent from
  // the Nylas grant this mailbox already has) is the only way to warm one.
  const isOAuthConnected = connectionType === 'oauth_google' || connectionType === 'oauth_microsoft'
  const oauthProviderLabel = connectionType === 'oauth_google' ? 'Google' : 'Microsoft'
  // A role with no warmup access at all (rep, analyst -- no PERMISSIONS
  // entry for "warmup.manage") must never see this section, and must never
  // even attempt the status fetch: that call would 403 "Not permitted" and
  // surface as "Could not load warmup status. Try again." below, exactly
  // the kind of show-then-fail this hides instead.
  // `allowed` decides whether the section renders at all; `canWrite` decides
  // whether anything in it may be changed. The cell is owner/admin = manage,
  // manager = view, so a Manager sees the card read-only rather than a form
  // whose every action would 403.
  const { allowed, canWrite } = usePermission('warmup', 'manage')
  const [account, setAccount] = useState<WarmupAccount | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isActing, setIsActing] = useState(false)
  // Separate from isActing: refreshing changes nothing, so it must not disable
  // Pause and Disconnect the way a real action does.
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Distinct from "no warmup account" (a 200 with a null body): set when the
  // status fetch fails, so a sender we can't load doesn't render the Connect
  // form that would only fail again on submit.
  const [loadError, setLoadError] = useState<string | null>(null)

  // Connect form state
  const [provider, setProvider] = useState<WarmupProviderType>('instantly')
  const [apiKey, setApiKey] = useState('')
  // Microsoft-only gate, mirroring Instantly's own connect flow: Instantly's
  // Microsoft OAuth still needs IMAP + Authenticated SMTP enabled on the
  // mailbox in the Microsoft 365 admin center (a per-mailbox toggle, separate
  // from the OAuth consent itself, and separate from the Nylas admin-consent
  // step this same mailbox already went through). Instantly's own dashboard
  // requires the equivalent "Yes, the SMTP has been enabled" confirmation
  // before it lets the OAuth button proceed, because skipping it is a common
  // silent-failure report on their side; this repeats that gate here instead
  // of finding out only after the consent screen closes.
  const [smtpEnabledConfirmed, setSmtpEnabledConfirmed] = useState(false)

  // OAuth connect (Nylas-brokered mailboxes only): "Waiting for you to finish
  // signing in" while Instantly's own consent tab is open and we're polling
  // for it to resolve. A ref (not state) for the interval id -- it never
  // needs to trigger a render, only to be cleared on success/error/unmount.
  const [oauthPending, setOauthPending] = useState(false)
  const oauthPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopOAuthPolling = () => {
    if (oauthPollRef.current) {
      clearInterval(oauthPollRef.current)
      oauthPollRef.current = null
    }
  }
  // Never leave a poll running past the component that started it -- a
  // closed tab or an abandoned consent screen must not poll forever.
  useEffect(() => stopOAuthPolling, [])

  // Disconnect is irreversible and takes the stored API key with it, so it
  // asks first (AB#22439). It used to be a one-click ghost button.
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)
  // Rotating the key in place, so an expired provider key does not cost the
  // warmup's history.
  const [rotating, setRotating] = useState(false)
  const [newApiKey, setNewApiKey] = useState('')

  const fetchStatus = async () => {
    try {
      const res = await warmupApi.getStatus(senderEmailId)
      // 200 with a null body legitimately means "no warmup account yet" -> the
      // Connect form should show. Only an actual error is a load failure.
      setAccount(res.data)
      setLoadError(null)
    } catch (err) {
      setAccount(null)
      // A 404 is the backend ownership guard: this sender_id isn't owned by the
      // current user (e.g. stale data from a previous session). Surfacing it
      // stops us from rendering a Connect form that would just 404 again.
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setLoadError("This sender email isn't available on your account. Try reloading the page.")
      } else {
        setLoadError('Could not load warmup status. Try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!allowed) return
    fetchStatus()
  }, [senderEmailId, allowed])

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error('API key is required')
      return
    }
    setIsActing(true)
    try {
      // No target: the backend ramps toward the sender's own cap, which comes
      // from the detected SMTP provider (routes/warmup.resolve_warmup_target).
      const data: ConnectWarmupData = { provider, api_key: apiKey }
      const res = await warmupApi.connect(senderEmailId, data)
      setAccount(res.data)
      setApiKey('')
      toast.success(`Connected to ${provider}`)
    } catch (err) {
      // Surface the backend's actual reason (e.g. "Sender email not found",
      // "must be SMTP verified", or the provider's own message) instead of a
      // generic string that hides what went wrong.
      toast.error(getErrorMessage(err, 'Failed to connect warmup provider'))
      // The commonest refusal is "this mailbox is already warmed through
      // another sender". The status call answers for the mailbox, so a refresh
      // replaces the form with that connection and its progress bar.
      await fetchStatus()
    } finally {
      setIsActing(false)
    }
  }

  const handleOAuthConnect = async () => {
    if (!apiKey.trim()) {
      toast.error('API key is required')
      return
    }
    if (connectionType === 'oauth_microsoft' && !smtpEnabledConfirmed) {
      toast.error('Confirm IMAP and Authenticated SMTP are enabled for this mailbox first')
      return
    }
    setIsActing(true)
    setOauthPending(true)
    try {
      const data: ConnectWarmupData = { provider: 'instantly', api_key: apiKey }
      const res = await warmupApi.initOAuth(senderEmailId, data)
      // A new tab, not a popup: Instantly's own hosted consent screen, no
      // different from any other "sign in with Google/Microsoft" redirect --
      // and immune to a popup blocker.
      window.open(res.data.auth_url, '_blank', 'noopener')

      const { oauth_session_id } = res.data
      oauthPollRef.current = setInterval(async () => {
        try {
          const poll = await warmupApi.pollOAuth(senderEmailId, oauth_session_id)
          if (poll.data.status === 'pending') return
          stopOAuthPolling()
          setIsActing(false)
          setOauthPending(false)
          if (poll.data.status === 'success' && poll.data.account) {
            setAccount(poll.data.account)
            setApiKey('')
            toast.success('Connected to instantly')
          } else {
            toast.error(poll.data.error || 'Could not complete the connection. Please try again.')
          }
        } catch (err) {
          stopOAuthPolling()
          setIsActing(false)
          setOauthPending(false)
          toast.error(getErrorMessage(err, 'Could not check the connection status'))
        }
      }, OAUTH_POLL_INTERVAL_MS)
    } catch (err) {
      setIsActing(false)
      setOauthPending(false)
      toast.error(getErrorMessage(err, 'Failed to start the connection'))
    }
  }

  // Refresh asks the PROVIDER, then shows what came back. It used to call
  // getStatus, which re-reads the row the 30-minute sweep last wrote -- so
  // the button moved nothing and a limit changed in Instantly stayed hidden
  // for up to half an hour.
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      const res = await warmupApi.sync(senderEmailId)
      setAccount(res.data)
      setLoadError(null)
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not refresh from the provider'))
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleStart = async () => {
    setIsActing(true)
    try {
      const res = await warmupApi.start(senderEmailId)
      setAccount(res.data)
      toast.success('Warmup started')
    } catch {
      toast.error('Failed to start warmup')
    } finally {
      setIsActing(false)
    }
  }

  const handlePause = async () => {
    setIsActing(true)
    try {
      const res = await warmupApi.pause(senderEmailId)
      setAccount(res.data)
      toast.success('Warmup paused')
    } catch {
      toast.error('Failed to pause warmup')
    } finally {
      setIsActing(false)
    }
  }

  const handleStop = async () => {
    setConfirmingDisconnect(false)
    setIsActing(true)
    try {
      await warmupApi.stop(senderEmailId)
      setAccount(null)
      toast.success('Warmup disconnected')
    } catch {
      toast.error('Failed to disconnect warmup')
    } finally {
      setIsActing(false)
    }
  }

  const handleRotateKey = async () => {
    const key = newApiKey.trim()
    if (!key) return
    setIsActing(true)
    try {
      const res = await warmupApi.updateApiKey(senderEmailId, { api_key: key })
      setAccount(res.data)
      setNewApiKey('')
      setRotating(false)
      toast.success('API key updated')
    } catch (e) {
      // The backend verifies the new key against the provider before storing
      // it, so a rejection here means the OLD key is still in place.
      toast.error(getErrorMessage(e, 'Could not update the API key'))
    } finally {
      setIsActing(false)
    }
  }

  if (!allowed) return null
  // A Nylas-brokered mailbox never sets smtp_verified — there's no SMTP
  // handshake for it — so the SMTP gate alone would hide this section for
  // every OAuth-connected sender forever, not just the not-yet-verified ones.
  if (!smtpVerified && !isOAuthConnected) return null
  if (isLoading) {
    return (
      <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border/50">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleNotch className="w-4 h-4 animate-spin" />
          Loading warmup status...
        </div>
      </div>
    )
  }

  // Status fetch failed (e.g. sender not owned by the current user). Show the
  // reason with a retry instead of the Connect form, which would only 404 again.
  if (loadError) {
    return (
      <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border/50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-red-500">
            <WarningCircle className="w-4 h-4 shrink-0" />
            <span>{loadError}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setLoadError(null); setIsLoading(true); fetchStatus() }}
            icon={<ArrowsClockwise className="w-3 h-3" />}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // Not connected — show connect form. A mailbox already warmed through
  // another sender never lands here: the status call answers for the mailbox
  // and returns that connection with is_own_connection=false instead of null.
  if (!account) {
    return (
      <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border/50 space-y-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-emphasis text-foreground">Email Warmup</h3>
        </div>
        {!canWrite && (
          <p className="text-xs text-muted-foreground">
            Only an Owner or Admin can connect warmup. Its progress will show here once it is set up.
          </p>
        )}
        {/* Warm-up reminder: this mailbox is already connected (smtpVerified ||
            isOAuthConnected gates the whole section, see above), but nothing
            is ramping it yet. New inboxes that go straight to cold outreach
            land in spam far more often, so this nudges toward Connect before
            the first campaign goes out, not after. */}
        <div className={warnBannerClass}>
          <div className={`${iconChipAmberClass} flex-shrink-0`}>
            <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" weight="fill" />
          </div>
          <div>
            <p className="font-emphasis text-amber-800 dark:text-amber-200" style={{ fontSize: '13px' }}>
              Warm up before sending cold emails
            </p>
            <p className="mt-0.5 text-amber-700/90 dark:text-amber-300/80" style={bannerTextStyle}>
              This mailbox isn&apos;t warming up yet. New inboxes usually need 2–3 weeks of
              warm-up before cold outreach to reduce spam risk.
            </p>
          </div>
        </div>
        {/* Target Daily Limit is no longer asked for. The ramp writes its volume
            onto the sender's daily_send_limit, so a number typed here walked
            that cap past whatever the real SMTP provider allows. It is derived
            from the sender's own detected cap instead. */}
        {isOAuthConnected ? (
          <>
            {/* No provider dropdown: this mailbox has no SMTP/IMAP password to
                give Warmbox or Lemwarm, so Instantly (the only one that offers
                its own OAuth connect) is the only option. */}
            <p className="text-xs text-muted-foreground">
              This mailbox is connected by OAuth, so warming it needs Instantly's own{' '}
              {oauthProviderLabel} sign-in — a separate step from connecting it here.
            </p>
            {/* Instantly's Microsoft OAuth still needs IMAP + Authenticated SMTP
                switched on for the mailbox in the Microsoft 365 admin center --
                a per-mailbox setting, unrelated to the OAuth consent screen and
                to the Nylas admin-consent step this mailbox already went
                through. Instantly's own dashboard gates its Microsoft connect
                button on the same confirmation, because skipping this step is
                the most common reason a Microsoft connect looks like it
                succeeded but the account never actually starts sending/reading
                mail. Google has no equivalent prerequisite, so this only shows
                for Microsoft. */}
            {connectionType === 'oauth_microsoft' && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-2">
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Before connecting: in the{' '}
                  <a
                    href="https://admin.microsoft.com/Adminportal/Home#/users"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Microsoft 365 admin center
                  </a>
                  , open this mailbox under Active users → Mail → Manage email apps, and check
                  both <strong>IMAP</strong> and <strong>Authenticated SMTP</strong>. This can take up
                  to an hour to take effect after you turn it on.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smtpEnabledConfirmed}
                    onChange={e => setSmtpEnabledConfirmed(e.target.checked)}
                    disabled={!canWrite}
                    className="mt-[3px] h-3.5 w-3.5 rounded-sm accent-amber-600 cursor-pointer shrink-0"
                  />
                  <span className="text-[11px] text-amber-700 dark:text-amber-400">
                    IMAP and Authenticated SMTP are enabled for this mailbox
                  </span>
                </label>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Instantly API Key</label>
              <Input
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={canWrite ? 'Enter your Instantly API key' : 'Owner or Admin only'}
                disabled={!canWrite}
                aria-label="Provider API key"
              />
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleOAuthConnect}
              disabled={
                !canWrite || isActing || !apiKey.trim() ||
                (connectionType === 'oauth_microsoft' && !smtpEnabledConfirmed)
              }
              icon={isActing ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            >
              {oauthPending ? 'Waiting for sign-in…' : `Connect with ${oauthProviderLabel}`}
            </Button>
            {oauthPending && (
              <p className="text-[10px] text-muted-foreground/70">
                Finish signing in with {oauthProviderLabel} in the new tab. This updates on its own
                once that's done.
              </p>
            )}
          </>
        ) : (
          <>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Provider</label>
              <Select
                value={provider}
                onChange={(v) => setProvider(v as WarmupProviderType)}
                options={PROVIDERS}
                ariaLabel="Warmup provider"
                className="w-full"
                disabled={!canWrite}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">API Key</label>
              <Input
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={canWrite ? 'Enter your provider API key' : 'Owner or Admin only'}
                disabled={!canWrite}
                aria-label="Provider API key"
              />
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleConnect}
              disabled={!canWrite || isActing || !apiKey.trim()}
              icon={isActing ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            >
              Connect
            </Button>
          </>
        )}
      </div>
    )
  }

  // Connected — show status and controls
  const statusConfig: Record<string, { label: string; className: string; dotColor: string }> = {
    inactive: { label: 'Connected', className: 'bg-muted/60 text-muted-foreground', dotColor: 'bg-muted-foreground' },
    warming: { label: 'Warming Up', className: 'bg-green-500/15 text-green-600 dark:text-green-400', dotColor: 'bg-green-500 animate-pulse' },
    paused: { label: 'Paused', className: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400', dotColor: 'bg-yellow-500' },
    completed: { label: 'Completed', className: 'bg-blue-500/15 text-blue-600 dark:text-blue-400', dotColor: 'bg-blue-500' },
    error: { label: 'Error', className: 'bg-red-500/15 text-red-600 dark:text-red-400', dotColor: 'bg-red-500' },
  }

  const cfg = statusConfig[account.status] || statusConfig.error
  const action = warmupAction(account.status)
  // The connection was made from another sender row for this same mailbox.
  // The progress is the mailbox's and is shown in full. Whoever holds the
  // mailbox holds the warmup, so an Owner or Admin may change it from this
  // card too; the backend resolves the mutations to the same row.
  const isOwn = account.is_own_connection !== false
  // Whether THIS user may change the warmup: the role decides, not the row.
  const editable = canWrite
  const progressPct = account.target_daily_limit > 0
    ? Math.min(100, Math.round((account.current_daily_limit / account.target_daily_limit) * 100))
    : 0

  return (
    <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border/50 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-emphasis text-foreground">Email Warmup</h3>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-emphasis ${cfg.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`} />
            {cfg.label}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground capitalize">{account.provider}</span>
      </div>

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">
            {account.current_daily_limit} / {account.target_daily_limit} emails/day
          </span>
          <span className="text-xs text-muted-foreground/60">{progressPct}%</span>
        </div>
        <div className="w-full bg-muted/50 rounded-full h-1.5">
          <div
            className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      {(account.inbox_rate !== undefined && account.inbox_rate !== null || account.spam_rate !== undefined && account.spam_rate !== null) && (
        <div className="flex gap-4 text-xs">
          {account.inbox_rate != null && (
            <span className="text-green-600 dark:text-green-400">
              Inbox: {account.inbox_rate}%
            </span>
          )}
          {account.spam_rate != null && (
            <span className="text-red-500">
              Spam: {account.spam_rate}%
            </span>
          )}
        </div>
      )}

      {/* Error message */}
      {account.error_message && (
        <p className="text-xs text-red-500">{account.error_message}</p>
      )}

      {/* Last synced */}
      {account.last_status_check && (
        <p className="text-[10px] text-muted-foreground/60">
          {/* The shared formatter, not a bare toLocaleString(). That pins
              the locale and spells the month out: an unpinned one renders
              "10/8/2026" on a US browser and "08/10/2026" on a UK one, the
              same instant read as two different dates depending on who is
              looking (AB#22513). Seconds are dropped too -- they are noise on
              a line that only says how fresh the figures are. */}
          Last synced: {formatDateTime(account.last_status_check)}
        </p>
      )}

      {/* API key — stored state and in-place rotation. The key itself is
          never sent to the browser; has_api_key only says whether one is on
          file, which the card previously could not say at all (AB#22439). */}
      <div className="pt-1 border-t border-border/40 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Key className="w-3.5 h-3.5" />
            {account.api_key_unreadable
              ? 'Stored API key cannot be read on this server. Enter it again with Update key.'
              : account.has_api_key === false
                ? 'No API key stored'
                : `API key stored for ${account.provider}`}
          </span>
          {!rotating && editable && (
            <Button variant="ghost" size="sm" onClick={() => setRotating(true)} disabled={isActing}>
              Update key
            </Button>
          )}
        </div>
        {!editable && (
          <>
            {/* A disabled, masked field says "a key is here and you cannot
                type into it" at a glance, which the sentence alone did not. */}
            <Input
              type="password"
              value="••••••••••••"
              disabled
              readOnly
              aria-label="Provider API key (read-only)"
            />
            <p className="text-[10px] text-muted-foreground/70">
              {isOwn
                ? 'Only an Owner or Admin can change the key or the warmup.'
                : `This mailbox is already connected to ${account.provider} through another sender using the same address. Only an Owner or Admin can change the key or the warmup.`}
            </p>
          </>
        )}
        {editable && !isOwn && (
          <p className="text-[10px] text-muted-foreground/70">
            Connected to {account.provider} through another sender using this same address.
            Changes made here apply to that one connection.
          </p>
        )}
        {rotating && (
          <div className="space-y-2">
            <Input
              type="password"
              value={newApiKey}
              onChange={e => setNewApiKey(e.target.value)}
              placeholder={`New ${account.provider} API key`}
              aria-label="New API key"
            />
            <p className="text-[10px] text-muted-foreground/70">
              Checked against {account.provider} before it replaces the stored one. If it is
              rejected, the current key stays as it is.
            </p>
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" onClick={handleRotateKey}
                disabled={isActing || !newApiKey.trim()}
                icon={isActing ? <CircleNotch className="w-3 h-3 animate-spin" /> : undefined}>
                Save key
              </Button>
              <Button variant="ghost" size="sm" disabled={isActing}
                onClick={() => { setRotating(false); setNewApiKey('') }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {editable && action?.kind === 'start' && (
          <Button variant="default" size="sm" onClick={handleStart} disabled={isActing}
            icon={isActing ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}>
            {action.label}
          </Button>
        )}
        {editable && action?.kind === 'pause' && (
          <Button variant="outline" size="sm" onClick={handlePause} disabled={isActing}
            icon={isActing ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Pause className="w-3 h-3" />}>
            {action.label}
          </Button>
        )}
        {editable && (
          <Button variant="ghost" size="sm" onClick={() => setConfirmingDisconnect(true)}
            disabled={isActing}
            icon={<Plugs className="w-3 h-3" />}>
            Disconnect
          </Button>
        )}
        {/* Beside Pause and Disconnect because it belongs to the same set of
            things you can do to this connection. Named as well as drawn: an
            icon alone would not say whether it reloads the page, the card, or
            the provider. */}
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isActing || isRefreshing}
          title="Fetch the current figures from the provider"
          icon={<ArrowsClockwise className={cn('w-3 h-3', isRefreshing && 'animate-spin')} />}>
          {isRefreshing ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* Disconnect deletes the warmup row, and the stored API key with it —
          the key is write-only, so there is no copy to restore from. Say that
          before doing it (AB#22439). */}
      <ConfirmDialog
        open={confirmingDisconnect}
        tone="destructive"
        title="Disconnect warmup?"
        description={
          <>
            This deletes the stored {account.provider} API key. It is never sent back to the
            browser, so there is no copy to restore — you will need the key again to reconnect.
            Warmup history and the provider's ramp progress are lost too.
            <br /><br />
            To replace an expired key, use <strong>Update key</strong> instead — it keeps both.
          </>
        }
        confirmLabel="Disconnect"
        loading={isActing}
        onConfirm={handleStop}
        onCancel={() => setConfirmingDisconnect(false)}
      />
    </div>
  )
}

export default WarmupSection
